/**
 * Кэш пары «карта + граф» и справочника компаний.
 *
 * Приложение обязано работать в павильоне, где связи нет. Поэтому пара
 * сохраняется при каждой удачной загрузке, а когда сеть недоступна —
 * читается отсюда.
 *
 * **Пара хранится одной записью.** Карта и граф порознь бессмысленны:
 * координаты в графе заданы в системе `viewBox` карты. Одна запись —
 * одна запись в хранилище, поэтому обновление всегда целое: либо там
 * новая пара, либо осталась прежняя. Половинчатого состояния — новой
 * карты со старым графом — не возникает, и старое исчезает ровно в тот
 * момент, когда встаёт новое.
 *
 * Хранилище — localStorage. Он даёт эту неделимость даром: `setItem`
 * либо заменил значение, либо бросил и оставил прежнее. Cache API
 * транзакций не даёт вовсе, IndexedDB дал бы, но ради той же гарантии
 * потребовал бы втрое больше кода. Объёмы позволяют: граф живой карты —
 * 130 КБ, сама карта — около 105 КБ.
 *
 * **Сверка при чтении та же, что при загрузке по сети.** Она же ловит
 * запись, собранную старой версией приложения, и пару, которая успела
 * разойтись в самом хранилище.
 */

import checkPair from '@/views/pages/MapView/navigation/checkPair';

/** Версия записи. Сменилась — прежние записи читать нельзя. */
const RECORD_VERSION = 1;

function storageKey(exhId, what) {
  return `exh-${exhId}-map-${what}`;
}

function read(exhId, what) {
  try {
    const raw = localStorage.getItem(storageKey(exhId, what));
    if (!raw) return null;

    const record = JSON.parse(raw);
    return record && record.v === RECORD_VERSION ? record.data : null;
  } catch (error) {
    return null;
  }
}

/**
 * Сносит записи карты других мероприятий.
 *
 * Пара — самое крупное, что приложение держит в хранилище: около 185 КБ
 * против десятка килобайт у всего остального. Посетитель смотрит одно
 * мероприятие, а записи копились бы по одной на каждое открытое, и
 * однажды место кончилось бы молча — запись не прошла бы, и карта
 * перестала работать без сети.
 */
function dropOtherEvents(exhId) {
  const mine = [storageKey(exhId, 'pair'), storageKey(exhId, 'directory')];

  try {
    Object.keys(localStorage)
      .filter((key) => /^exh-.+-map-(pair|directory)$/.test(key) && !mine.includes(key))
      .forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    // не вышло прибраться — не повод не записывать
  }
}

function write(exhId, what, data) {
  const key = storageKey(exhId, what);
  const raw = JSON.stringify({ v: RECORD_VERSION, data });

  dropOtherEvents(exhId);

  try {
    localStorage.setItem(key, raw);
    return true;
  } catch (error) {
    // Место кончилось: прежняя запись тут уже не поможет, а мешать
    // может. Освобождаем и пробуем один раз.
    try {
      localStorage.removeItem(key);
      localStorage.setItem(key, raw);
      return true;
    } catch (retryError) {
      return false;
    }
  }
}

/**
 * Пара из кэша. Не сошлась — считаем, что кэша нет: лучше остаться без
 * навигации, чем вести по чужой карте.
 * @returns {Array|null}
 */
export function readPair(exhId) {
  const maps = read(exhId, 'pair');
  if (!Array.isArray(maps) || !maps.length) return null;

  const broken = maps
    .filter((item) => item && item.nav_graph)
    .map((item) => checkPair(item.svg_map, item.nav_graph, item.map_name))
    .find((result) => !result.ok);

  if (broken) {
    console.warn('[карта] пара в кэше не сошлась, кэш отброшен:', broken.problems.join('; '));
    try {
      localStorage.removeItem(storageKey(exhId, 'pair'));
    } catch (error) {
      // не вышло убрать — чтение всё равно вернёт null
    }
    return null;
  }

  return maps;
}

export function writePair(exhId, maps) {
  if (!Array.isArray(maps) || !maps.length) return false;
  return write(exhId, 'pair', maps);
}

/** Справочник компаний: без него поиск по названию не работает офлайн. */
export function readDirectory(exhId) {
  const directory = read(exhId, 'directory');
  return Array.isArray(directory) ? directory : null;
}

export function writeDirectory(exhId, directory) {
  if (!Array.isArray(directory) || !directory.length) return false;
  return write(exhId, 'directory', directory);
}
